import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  FileText,
  Hourglass,
  TrendingUp,
  Zap,
} from "lucide-react";
import { DashboardMetricCard } from "../dashboard/DashboardMetricCard";

type MetricsLabels = {
  admissions: string;
  allInvoices: string;
  outOfPackageInvoices: string;
  inProgress: string;
  validated: string;
  savings: string;
  savingsRequested: string;
  savingsValidated: string;
  savingsGap: string;
  dailyAttendance: string;
};

type MetricsData = {
  admissions: { value: string };
  allInvoices: { value: string; amount: string };
  outOfPackageInvoices: { value: string; amount: string };
  inProgress: { value: string; amount: string };
  validated: { value: string; amount: string };
  savings: { value: string; footer: string; requested: string; validated: string };
};

type EnterpriseDashboardMetricsProps = {
  labels: MetricsLabels;
  data: MetricsData;
};

const metricsConfig = {
  admissions: {
    cardClassName:
      "!border-green !bg-green shadow-[0_8px_24px_rgba(184,228,23,0.22)] hover:!brightness-[0.97]",
    iconClassName: "rounded-2xl bg-white/45 text-primary shadow-sm",
    valueClassName: "text-primary",
    titleClassName: "font-semibold text-primary",
    href: "/dashboard/admissions",
  },
  allInvoices: {
    cardClassName:
      "!border-primary/45 !bg-white hover:!border-primary/65 dark:!bg-white",
    iconClassName: "rounded-2xl bg-primary text-white shadow-sm",
    valueClassName: "text-primary",
    titleClassName: "font-semibold text-primary",
    href: "/dashboard/invoices",
  },
  outOfPackageInvoices: {
    cardClassName:
      "!border-red-500/40 !bg-red-50 hover:!border-red-500/55 dark:!bg-red-500/10",
    iconClassName: "rounded-2xl bg-red-500/15 text-red-600 shadow-sm dark:text-red-400",
    valueClassName: "text-red-600 dark:text-red-400",
    titleClassName: "font-semibold",
  },
  inProgress: {
    cardClassName:
      "!border-yellow-400/50 !bg-white hover:!border-yellow-400/65 dark:!border-yellow-500/40 dark:!bg-white",
    iconClassName: "rounded-2xl bg-yellow-400/30 text-amber-800 shadow-sm dark:text-primary",
    titleClassName: "font-semibold text-foreground",
  },
  validated: {
    cardClassName:
      "!border-green/45 !bg-white hover:!border-green/55 dark:!bg-white",
    iconClassName: "rounded-2xl bg-green/25 text-primary shadow-sm",
    valueClassName: "text-primary",
    titleClassName: "font-semibold text-primary",
    href: "/dashboard/invoices/approved",
  },
  savings: {
    cardClassName:
      "!border-green/45 !bg-white hover:!border-green/55 dark:!bg-white",
    iconClassName: "rounded-2xl bg-green/25 text-primary shadow-sm",
    valueClassName: "text-primary",
    titleClassName: "font-semibold text-primary",
  },
} as const;

export function EnterpriseDashboardMetrics({ labels, data }: EnterpriseDashboardMetricsProps) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <DashboardMetricCard
        title={labels.admissions}
        value={data.admissions.value}
        footer={
          <span className="inline-flex items-center gap-1 font-medium text-primary">
            <TrendingUp size={13} className="text-primary" />
            {labels.dailyAttendance}
          </span>
        }
        icon={CalendarDays}
        {...metricsConfig.admissions}
      />
      <DashboardMetricCard
        title={labels.allInvoices}
        value={data.allInvoices.value}
        footer={
          <span className="inline-flex items-center gap-1 font-semibold text-primary">
            <FileText size={13} className="text-primary" />
            {data.allInvoices.amount}
          </span>
        }
        icon={FileText}
        footerClassName="justify-end"
        {...metricsConfig.allInvoices}
      />
      <DashboardMetricCard
        title={labels.outOfPackageInvoices}
        value={data.outOfPackageInvoices.value}
        footer={
          <span className="inline-flex items-center gap-1 font-semibold text-red-600 dark:text-red-400">
            <FileText size={13} />
            {data.outOfPackageInvoices.amount}
          </span>
        }
        icon={AlertCircle}
        footerClassName="justify-end"
        {...metricsConfig.outOfPackageInvoices}
      />
      <DashboardMetricCard
        title={labels.inProgress}
        value={data.inProgress.value}
        footer={
          <span className="inline-flex items-center gap-1 font-semibold text-foreground">
            <Hourglass size={13} className="text-muted" />
            {data.inProgress.amount}
          </span>
        }
        icon={Clock3}
        footerClassName="justify-end"
        {...metricsConfig.inProgress}
      />
      <DashboardMetricCard
        title={labels.validated}
        value={data.validated.value}
        footer={
          <span className="inline-flex items-center gap-1 font-semibold text-primary">
            <TrendingUp size={13} className="text-green" />
            {data.validated.amount}
          </span>
        }
        icon={CheckCircle2}
        footerClassName="justify-end"
        {...metricsConfig.validated}
      />
      <DashboardMetricCard
        title={labels.savings}
        value={data.savings.value}
        footer={
          <span className="flex w-full flex-col gap-0.5">
            <span className="flex justify-between gap-3">
              <span>{labels.savingsRequested}</span>
              <span className="font-semibold text-foreground">{data.savings.requested}</span>
            </span>
            <span className="flex justify-between gap-3">
              <span>{labels.savingsValidated}</span>
              <span className="font-semibold text-foreground">{data.savings.validated}</span>
            </span>
            <span className="flex justify-between gap-3 font-semibold text-primary">
              <span>{labels.savingsGap}</span>
              <span>{data.savings.footer}</span>
            </span>
          </span>
        }
        icon={Zap}
        footerClassName="items-stretch"
        {...metricsConfig.savings}
      />
    </div>
  );
}
